import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Shield, Users, Search, ArrowLeft, Crown,
  ArrowUpRight, Ban, XCircle, CheckCircle, Gift, Send, X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_DISPLAY = { starter: "Investor", pro: "Investor", investor: "Investor", operator: "Operator" };
const SUB_STATUS_COLOR = {
  active: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  trialing: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  cancelled: "text-red-400 bg-red-500/10 border-red-500/30",
  payment_failed: "text-orange-400 bg-orange-500/10 border-orange-500/30",
  inactive: "text-white/40 bg-white/[0.04] border-white/[0.08]",
};

export default function AdminUsers({ user, subscription, onLogout }) {
  const { isCollapsed } = useSidebar();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => { fetchUsers(); }, [searchTerm, tierFilter]);

  const fetchUsers = async () => {
    try {
      let url = `${BACKEND_URL}/api/admin/users?`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (tierFilter !== "all") url += `tier=${tierFilter}`;
      const response = await axios.get(url, { withCredentials: true });
      setUsers(response.data.users || response.data || []);
    } catch {
      toast.error("Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTier = async (userId, newTier) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${userId}`, { subscription_tier: newTier }, { withCredentials: true });
      toast.success(`Tier updated to ${TIER_DISPLAY[newTier] || newTier}`);
      fetchUsers();
    } catch { toast.error("Failed to update tier"); }
  };

  const handleBan = async (userId, isBanned) => {
    setActionLoading(`ban-${userId}`);
    try {
      const endpoint = isBanned ? "unban" : "ban";
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/${endpoint}`, {}, { withCredentials: true });
      toast.success(isBanned ? "User unbanned" : "User banned");
      fetchUsers();
    } catch { toast.error("Action failed"); }
    finally { setActionLoading(null); }
  };

  const handleCancelSub = async (userId) => {
    if (!window.confirm("Cancel this user's subscription? This cannot be undone.")) return;
    setActionLoading(`cancel-${userId}`);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/cancel-subscription`, {}, { withCredentials: true });
      toast.success("Subscription cancelled");
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel subscription"); }
    finally { setActionLoading(null); }
  };

  const handleGrantFreeAccess = async (userId, userName) => {
    if (!window.confirm(`Grant 7-day free trial to ${userName}? They will have full access for 7 days.`)) return;
    setActionLoading(`free-${userId}`);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/grant-free-access`, {}, { withCredentials: true });
      toast.success(`7-day trial granted to ${userName}`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to grant access"); }
    finally { setActionLoading(null); }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) { toast.error("Enter an email address"); return; }
    setSendingInvite(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/send-invite`, { email: inviteEmail.trim(), name: inviteName.trim() || undefined }, { withCredentials: true });
      toast.success(`Invite sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail("");
      setInviteName("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send invite"); }
    finally { setSendingInvite(false); }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'} p-4 md:p-8`} data-testid="admin-users">
        <div className="mb-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-white/70 hover:text-gold mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold text-white uppercase">Platform Users</h1>
              <p className="text-white/40 mt-1">{users.length} user{users.length !== 1 ? 's' : ''} registered</p>
            </div>
            <Button
              onClick={() => setShowInviteModal(true)}
              className="bg-[#D4AF37] hover:bg-[#C9A227] text-black font-bold gap-2"
              data-testid="send-invite-btn"
            >
              <Send className="w-4 h-4" /> Send Invite
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#0A0A0A]/80 border-white/[0.08] text-white"
              placeholder="Search by name or email..."
              data-testid="search-users"
            />
          </div>
          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger className="w-48 bg-[#0A0A0A]/80 border-white/[0.08] text-white">
              <SelectValue placeholder="All Tiers" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/[0.08]">
              <SelectItem value="all" className="text-white">All Tiers</SelectItem>
              <SelectItem value="pro" className="text-white">Investor</SelectItem>
              <SelectItem value="operator" className="text-white">Operator</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-[#0A0A0A]/80 border border-white/[0.08] overflow-x-auto rounded-lg">
            <table className="w-full min-w-[700px]">
              <thead className="bg-black">
                <tr>
                  <th className="text-left p-4 text-xs font-bold text-white/50 uppercase tracking-wider">User</th>
                  <th className="text-left p-4 text-xs font-bold text-white/50 uppercase tracking-wider">Tier</th>
                  <th className="text-left p-4 text-xs font-bold text-white/50 uppercase tracking-wider">Status</th>
                  <th className="text-left p-4 text-xs font-bold text-white/50 uppercase tracking-wider">Joined</th>
                  <th className="text-right p-4 text-xs font-bold text-white/50 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {users.map((u) => {
                  const isBanned = u.is_banned;
                  const subStatus = u.subscription_status || "inactive";
                  return (
                    <tr key={u.user_id} className={`hover:bg-white/[0.03] ${isBanned ? "opacity-50" : ""}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-white/[0.06] rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-gold">{u.name?.charAt(0) || "U"}</span>
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm flex items-center gap-1.5">
                              {u.name}
                              {isBanned && <span className="text-[10px] bg-red-500/20 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded">BANNED</span>}
                              {u.has_founder_badge && <Crown className="w-3 h-3 text-gold" />}
                            </p>
                            <p className="text-xs text-white/50">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Select value={['pro','operator'].includes(u.subscription_tier) ? u.subscription_tier : 'pro'} onValueChange={(v) => handleUpdateTier(u.user_id, v)}>
                          <SelectTrigger className={`w-32 text-sm border ${u.subscription_tier === 'operator' ? 'bg-purple-500/10 border-purple-500/30 text-purple-400' : 'bg-gold/10 border-gold/30 text-gold'}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/[0.08]">
                            <SelectItem value="pro" className="text-gold">Investor</SelectItem>
                            <SelectItem value="operator" className="text-purple-400">Operator</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-semibold border px-2 py-1 rounded capitalize ${SUB_STATUS_COLOR[subStatus] || SUB_STATUS_COLOR.inactive}`}>
                            {subStatus}
                          </span>
                          {u.free_access_granted && (
                            <span className="text-xs font-semibold border px-2 py-1 rounded text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30">
                              Free
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-white/50">{formatDate(u.created_at)}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {/* Grant Free Access — show for inactive/cancelled users */}
                          {(subStatus === "inactive" || subStatus === "cancelled") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleGrantFreeAccess(u.user_id, u.name)}
                              disabled={actionLoading === `free-${u.user_id}`}
                              title="Grant free access"
                              className="text-[#D4AF37] hover:text-[#F2D06B]"
                              data-testid={`grant-access-${u.user_id}`}
                            >
                              <Gift className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Ban / Unban */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleBan(u.user_id, isBanned)}
                            disabled={actionLoading === `ban-${u.user_id}`}
                            title={isBanned ? "Unban user" : "Ban user"}
                            className={isBanned ? "text-emerald-400 hover:text-emerald-300" : "text-red-400 hover:text-red-300"}
                            data-testid={`ban-user-${u.user_id}`}
                          >
                            {isBanned ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                          </Button>
                          {/* Cancel Subscription */}
                          {subStatus === "active" || subStatus === "trialing" ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancelSub(u.user_id)}
                              disabled={actionLoading === `cancel-${u.user_id}`}
                              title="Cancel subscription"
                              className="text-orange-400 hover:text-orange-300"
                              data-testid={`cancel-sub-${u.user_id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          ) : null}
                          {/* View */}
                          <Link to={`/admin/users/${u.user_id}`}>
                            <Button variant="ghost" size="sm" className="text-white/50 hover:text-gold">
                              <ArrowUpRight className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-white/30 mx-auto mb-3" />
                <p className="text-white/50">No users found</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Send Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-white font-bold text-lg">Send Trial Invite</h2>
                <p className="text-white/40 text-xs mt-0.5">They get 7 days free, then must subscribe</p>
              </div>
              <button onClick={() => setShowInviteModal(false)} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Email Address *</label>
                <Input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendInvite()}
                  placeholder="their@email.com"
                  type="email"
                  className="bg-black/60 border-white/[0.08] text-white focus:border-[#D4AF37]/40"
                  data-testid="invite-email-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Name (optional)</label>
                <Input
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="First Last"
                  className="bg-black/60 border-white/[0.08] text-white focus:border-[#D4AF37]/40"
                  data-testid="invite-name-input"
                />
              </div>

              <div className="bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 rounded-lg px-4 py-3 text-xs text-white/50">
                A magic link will be emailed to them. Once they register, they get <span className="text-[#D4AF37] font-semibold">7 days of full access</span>. After that they'll be prompted to subscribe at $99/mo.
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="ghost" onClick={() => setShowInviteModal(false)} className="flex-1 text-white/50 border border-white/[0.08]">
                  Cancel
                </Button>
                <Button
                  onClick={handleSendInvite}
                  disabled={sendingInvite || !inviteEmail.trim()}
                  className="flex-1 bg-[#D4AF37] hover:bg-[#C9A227] text-black font-bold gap-2"
                  data-testid="confirm-send-invite-btn"
                >
                  {sendingInvite ? "Sending..." : <><Send className="w-4 h-4" /> Send Invite</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
